import React, { createContext, useState, useContext, useRef } from 'react';

export type InputFilter = {[key: string]: string}
type SearchParamsType = {
    [key: string]: string | null;
  };
const InitialMessageType : InputFilter = { };

type FilterContextType = {
    filterData: InputFilter;
    initialFiltersSet: boolean;
    initialFiltersKey: string | null;
    updateFilter: (name: string, value: string) => void;
    deleteFilter: (name: string) => void;
    findFilterFromParams: (paramNames: string[]) => SearchParamsType;
    setInitialFilters: (paramNames: string[], params : URLSearchParams, storagePath?: string) => void;
    registerFilterDefaults: (defaults: InputFilter) => void;
    getUrlParameterString: (paramNames: string[]) => string;
    getFilterStorageKey: (pathname?: string) => string;
};

const FILTER_STORAGE_PREFIX = "AdminFiltererFilters";

const normalizeStoragePath = (pathname?: string): string => {
    const path = pathname || (typeof window !== "undefined" ? window.location.pathname : "") || "/";
    return path.replace(/\/+$/, "") || "/";
};

const getFilterStorageKey = (pathname?: string): string => {
    return `${FILTER_STORAGE_PREFIX}:${normalizeStoragePath(pathname)}`;
};

const readStoredFilters = (storageKey: string): InputFilter => {
    if (typeof window === "undefined") {
        return {};
    }

    try {
        const storedFilters = window.localStorage.getItem(storageKey);
        if (!storedFilters) {
            return {};
        }

        const parsedFilters = JSON.parse(storedFilters);
        if (!parsedFilters || typeof parsedFilters !== "object" || Array.isArray(parsedFilters)) {
            return {};
        }

        return Object.entries(parsedFilters).reduce<InputFilter>((filters, [key, value]) => {
            if (typeof value === "string" && value !== "") {
                filters[key] = value;
            }
            return filters;
        }, {});
    } catch {
        return {};
    }
};

const writeStoredFilters = (storageKey: string, filters: InputFilter) => {
    if (typeof window === "undefined") {
        return;
    }

    const filtersToStore = Object.entries(filters).reduce<InputFilter>((storedFilters, [key, value]) => {
        if (value !== "") {
            storedFilters[key] = value;
        }
        return storedFilters;
    }, {});

    try {
        if (Object.keys(filtersToStore).length === 0) {
            window.localStorage.removeItem(storageKey);
            return;
        }
        window.localStorage.setItem(storageKey, JSON.stringify(filtersToStore));
    } catch {
        // Browsers can deny localStorage access; filtering should still work in memory.
    }
};

// Create a context with a default value
const FilterContext = createContext<FilterContextType>({
    filterData: InitialMessageType,
    initialFiltersSet: false,
    initialFiltersKey: null,
    updateFilter: (name: string, value: string) => {},
    deleteFilter: (name: string) => {},
    findFilterFromParams: (paramNames: string[]) => { return {} as SearchParamsType },
    setInitialFilters: (paramNames: string[], params : URLSearchParams, storagePath?: string) => {},
    registerFilterDefaults: (defaults: InputFilter) => {},
    getUrlParameterString: (paramNames: string[]) => { return "" as string },
    getFilterStorageKey,
});


  
// This stores the filter data and provides functions to update it
// and to find filters from URL parameters
export const FilterProvider = ({ children }: { children: React.ReactNode }) => {
    const [filterData, setFilterData] = useState<InputFilter>(InitialMessageType);
    const [initialFiltersSet, setInitialFiltersSet] = useState<boolean>(false);
    const [initialFiltersKey, setInitialFiltersKey] = useState<string | null>(null);
    const activeStorageKey = useRef<string | null>(null);
    // Defaults declared on AdminFilter definitions (e.g. { default: "false" }). The dropdown shows a
    // default when no value is set, so the same default must be part of the query or the UI lies.
    const filterDefaults = useRef<InputFilter>({});
    const initializedRef = useRef<boolean>(false);

    const registerFilterDefaults = (defaults: InputFilter) => {
        filterDefaults.current = defaults;
        if (!initializedRef.current) { return; }
        // Filters were initialised before the defaults arrived: fill in whatever is still missing.
        setFilterData(prev => {
            const missing = Object.entries(defaults).filter(([name, value]) => value !== "" && prev[name] === undefined);
            if (missing.length === 0) { return prev; }
            const nextFilters = { ...prev, ...Object.fromEntries(missing) };
            if (activeStorageKey.current) {
                writeStoredFilters(activeStorageKey.current, nextFilters);
            }
            return nextFilters;
        });
    };

    const setInitialFilters = (paramNames: string[], params : URLSearchParams, storagePath?: string) => {
        const storageKey = getFilterStorageKey(storagePath);
        const storedFilters = readStoredFilters(storageKey);
        const hasRelevantUrlParams = paramNames.some((name) => params.has(name));
        const initialFilters = paramNames.reduce<InputFilter>((filters, name) => {
            const value = params.get(name);
            if (params.has(name)) {
                if (value !== null && value !== "") {
                    filters[name] = value;
                }
            } else if (!hasRelevantUrlParams && storedFilters[name] !== undefined) {
                filters[name] = storedFilters[name];
            } else if (filterDefaults.current[name]) {
                filters[name] = filterDefaults.current[name];
            }
            return filters;
        }, {});
        initializedRef.current = true;

        activeStorageKey.current = storageKey;
        writeStoredFilters(storageKey, initialFilters);
        setFilterData(initialFilters);
        setInitialFiltersKey(storageKey);
        setInitialFiltersSet(true);
    }

    const updateFilter = (name : string, value : string) => {
        setFilterData(prev => {
            const nextFilters = { ...prev, [name]: value };
            if (activeStorageKey.current) {
                writeStoredFilters(activeStorageKey.current, nextFilters);
            }
            return nextFilters;
        });
    };

    const deleteFilter = (name : string) => {
        setFilterData(prev => {
            const { [name]: _, ...rest } = prev;
            if (activeStorageKey.current) {
                writeStoredFilters(activeStorageKey.current, rest);
            }
            return rest;
        });
    }
    const findFilterFromParams = (paramNames: string[]) => {
        const filterParamNames = [...paramNames, "sort_by"];
        const searchParams: SearchParamsType = {};
        filterParamNames.forEach((name) => {
            searchParams[name] = filterData.hasOwnProperty(name) ? filterData[name] : null;
          });
        return searchParams
    }

    const getUrlParameterString = (paramNames: string[]) => {
        const searchParams: SearchParamsType = findFilterFromParams(paramNames);
        const urlParams = new URLSearchParams();
        Object.entries(searchParams).forEach(([key, value]) => {
            if (value !== null) {
                urlParams.append(key, value);
            }
        });
        return urlParams.toString();
    }

    return (
      <FilterContext.Provider value={{ filterData, initialFiltersSet, initialFiltersKey, updateFilter, deleteFilter, findFilterFromParams, setInitialFilters, registerFilterDefaults, getUrlParameterString, getFilterStorageKey }}>
        {children}
      </FilterContext.Provider>
    );
  };
export const useFilter = () => useContext(FilterContext);
